import { createContext, useContext, useSyncExternalStore, type ReactNode } from 'react';
import type { Principal } from '../api/types';
import type { AuthState, AuthStore } from './auth-store';

const AuthContext = createContext<AuthStore | null>(null);

/** Provides the AuthStore; routes gate on it in `beforeLoad`, screens read it with the hooks. */
export function AuthBoundary({ store, children }: { store: AuthStore; children: ReactNode }) {
  return <AuthContext.Provider value={store}>{children}</AuthContext.Provider>;
}

export function useAuthStore(): AuthStore {
  const s = useContext(AuthContext);
  if (!s) throw new Error('useAuthStore must be used inside <AuthBoundary>');
  return s;
}

export function useAuth(): AuthState {
  const store = useAuthStore();
  return useSyncExternalStore(store.subscribe, store.getState, store.getState);
}

/**
 * The signed-in principal; routes under the auth gate never render without one. For the moment
 * between the session's end and the next screen, it is the person on their way out
 * (`AuthStore.departing`), so the screen they are leaving does not fail under them.
 */
export function usePrincipal(): Principal {
  const store = useAuthStore();
  const principal = useAuth().principal ?? store.departing();
  if (!principal) throw new Error('usePrincipal outside a signed-in route');
  return principal;
}

/** Staff detection by claim: the API sets `staff` from the staff tenant and the Workspace domain. */
export function isStaff(p: Principal | null): boolean {
  return Boolean(p?.staff);
}
